/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import SegmentModel from 'cio/models/segment';
import RouterService from '@ember/routing/router-service';
import ApiErrorsService from 'cio/services/api-errors';
import DS from 'ember-data';
import AudienceModel from 'cio/models/audience';
import { tracked } from '@glimmer/tracking';
import { Adwords, Facebook } from 'cio/components/segments/create-audience/select-network';
import Ember from 'ember';
import { Task } from 'ember-concurrency';
import AuthService from 'cio/services/auth';
import { TYPES } from 'cio/utils/ad-audiences';

const STEP_SELECT_NETWORK = 'select-network';
const STEP_CONFIGURE_AUDIENCE = 'configure-audience';

interface CreateAudienceArgs {
  segment: SegmentModel;
  createAudienceTask: Task<AudienceModel, [any]>;
  onClose(): void;

  // used only for tests
  forceStep?: typeof STEP_SELECT_NETWORK | typeof STEP_CONFIGURE_AUDIENCE;
}

export default class CreateAudience extends Component<CreateAudienceArgs> {
  @service store!: DS.Store;
  @service router!: RouterService;
  @service apiErrors!: ApiErrorsService;
  @service auth!: AuthService;

  @tracked currentStep = STEP_SELECT_NETWORK;
  @tracked selectedNetwork: Facebook | Adwords | null = null;
  @tracked adAccountId?: string | null;
  @tracked name?: string;
  @tracked description?: string;
  @tracked googleAdwordsConsent = false;

  constructor(owner: unknown, args: CreateAudienceArgs) {
    super(owner, args);

    if (Ember.testing && this.args.forceStep) {
      this.currentStep = this.args.forceStep;
    }
  }

  get isReady() {
    return this.selectedNetwork?.isValid != null && !isEmpty(this.name);
  }

  get isSelectingNetwork() {
    return this.currentStep === STEP_SELECT_NETWORK;
  }

  get isConfiguringAudience() {
    return this.currentStep === STEP_CONFIGURE_AUDIENCE;
  }

  private getDefaultAudienceAttributes() {
    let { segment } = this.args;
    let path = this.router.urlFor('segments.segment', segment);
    return {
      name: `${segment.name} - Customer.io`,
      description: `${window.location.origin}${path}`,
    };
  }

  private goToStep(step: typeof STEP_SELECT_NETWORK | typeof STEP_CONFIGURE_AUDIENCE) {
    this.currentStep = step;
    this.args.createAudienceTask.cancelAll({ resetState: true });
  }

  get createAudienceDisabled() {
    return (
      !this.isReady ||
      this.args.createAudienceTask.isRunning ||
      !this.auth.hasPermission('journeys/segments/edit') ||
      (this.selectedNetwork?.type === TYPES.adwords && !this.googleAdwordsConsent)
    );
  }

  @action
  onSelectNetwork(network: Facebook | Adwords) {
    // If the user is selecting a different network, reset the adAccountId too.
    if (this.selectedNetwork !== network) {
      this.adAccountId = null;
    }

    this.selectedNetwork = network;
  }

  @action
  selectNetwork() {
    this.goToStep(STEP_SELECT_NETWORK);
  }

  @action
  configureAudience() {
    let { name, description } = this.getDefaultAudienceAttributes();
    this.name = name;
    this.description = description;

    this.goToStep(STEP_CONFIGURE_AUDIENCE);
  }

  @action
  createAudience() {
    let { name, description, adAccountId } = this;
    return this.args.createAudienceTask.perform({
      name,
      description,
      adAccountId,
      segment: this.args.segment,
      type: this.selectedNetwork?.type,
    });
  }

  @action
  onCloseAction() {
    if (this.args.createAudienceTask.isRunning) {
      return;
    }

    this.args.onClose();
  }
}
